/** Uma nuvem de pontos de exemplo, feita a partir do próprio modelo.
 *
 *  Serve para experimentar a comparação sem ter um levantamento à mão:
 *  pontos espalhados pelas faces dos pilares e das vigas, com o ruído
 *  de um laser, e com desvios que se sabem de antemão (um pilar fugido,
 *  outro desaprumado, uma viga descida, um pilar que o laser não viu).
 *  Quem abre o relatório pode ver se ele encontra o que lá se pôs.
 *
 *  Puro: recebe triângulos e devolve pontos, na mesma origem.
 */

export interface TriangulosDeUmElemento {
  localId: number
  positions: Float32Array
  indices: Uint32Array
}

/** Um gerador de números pseudo-aleatórios com semente (Park e Miller),
 *  para a nuvem de exemplo ser sempre a mesma. */
export function aleatorioComSemente(semente = 1): () => number {
  let s = semente % 2147483647
  if (s <= 0) s += 2147483646
  return () => (s = (s * 16807) % 2147483647) / 2147483647
}

/** Ruído de laser, aproximadamente normal (soma de seis uniformes). */
export function gaussiano(aleatorio: () => number): number {
  let soma = 0
  for (let i = 0; i < 6; i += 1) soma += aleatorio()
  return (soma - 3) / Math.SQRT1_2
}

/** A área de um elemento, em metros quadrados. */
export function area(e: TriangulosDeUmElemento): number {
  const p = e.positions, ix = e.indices
  let total = 0
  for (let t = 0; t + 2 < ix.length; t += 3) {
    const a = ix[t] * 3, b = ix[t + 1] * 3, c = ix[t + 2] * 3
    const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2]
    const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2]
    total += 0.5 * Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx)
  }
  return total
}

/** Mexe num ponto: recebe-o e devolve onde ele fica. */
export type Mexer = (x: number, y: number, z: number) => [number, number, number]

/**
 * Espalhar pontos pelas faces de um elemento, na proporção da área de
 * cada triângulo, e mexer-lhes como se pedir. Acrescenta a `saida`.
 *
 * @param densidade pontos por metro quadrado
 * @param ruido     desvio-padrão do ruído, em metros
 * @param maximo    tecto de pontos por elemento, para uma laje grande
 *                  não encher a nuvem
 */
export function amostrarElemento(
  e: TriangulosDeUmElemento,
  densidade: number,
  ruido: number,
  aleatorio: () => number,
  saida: number[],
  mexer: Mexer | null = null,
  maximo = 20_000,
): number {
  const p = e.positions, ix = e.indices
  const acumulada: number[] = []
  let total = 0
  for (let t = 0; t + 2 < ix.length; t += 3) {
    const a = ix[t] * 3, b = ix[t + 1] * 3, c = ix[t + 2] * 3
    const ux = p[b] - p[a], uy = p[b + 1] - p[a + 1], uz = p[b + 2] - p[a + 2]
    const vx = p[c] - p[a], vy = p[c + 1] - p[a + 1], vz = p[c + 2] - p[a + 2]
    total += 0.5 * Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx)
    acumulada.push(total)
  }
  if (total <= 0) return 0
  const n = Math.min(maximo, Math.round(total * densidade))
  for (let k = 0; k < n; k += 1) {
    // O triângulo, sorteado pela área (pesquisa binária na acumulada).
    const r = aleatorio() * total
    let lo = 0, hi = acumulada.length - 1
    while (lo < hi) {
      const m = (lo + hi) >> 1
      if (acumulada[m] < r) lo = m + 1
      else hi = m
    }
    const t = lo * 3
    const a = ix[t] * 3, b = ix[t + 1] * 3, c = ix[t + 2] * 3
    let u = aleatorio(), v = aleatorio()
    if (u + v > 1) {
      u = 1 - u
      v = 1 - v
    }
    let x = p[a] + u * (p[b] - p[a]) + v * (p[c] - p[a])
    let y = p[a + 1] + u * (p[b + 1] - p[a + 1]) + v * (p[c + 1] - p[a + 1])
    let z = p[a + 2] + u * (p[b + 2] - p[a + 2]) + v * (p[c + 2] - p[a + 2])
    if (mexer) [x, y, z] = mexer(x, y, z)
    saida.push(x + gaussiano(aleatorio) * ruido, y + gaussiano(aleatorio) * ruido, z + gaussiano(aleatorio) * ruido)
  }
  return n
}

/** O que se fez a cada elemento, para se poder conferir no relatório. */
export interface CasoConhecido {
  localId: number
  descricao: string
}

/** O ponto mais baixo de um elemento, em y (o cimo). */
function base(e: TriangulosDeUmElemento): number {
  let m = Infinity
  for (let i = 1; i < e.positions.length; i += 3) m = Math.min(m, e.positions[i])
  return m
}

function altura(e: TriangulosDeUmElemento): number {
  let lo = Infinity, hi = -Infinity
  for (let i = 1; i < e.positions.length; i += 3) {
    lo = Math.min(lo, e.positions[i])
    hi = Math.max(hi, e.positions[i])
  }
  return hi - lo
}

/**
 * A nuvem de exemplo. Os casos saem dos pilares mais altos e da viga
 * mais comprida, para os desvios se verem bem.
 *
 * @returns os pontos (xyz seguidos, na origem dos triângulos) e os
 *          casos que se puseram.
 */
export function nuvemDeExemplo(
  pilares: TriangulosDeUmElemento[],
  vigas: TriangulosDeUmElemento[],
  opcoes: { densidade?: number; ruido?: number; semente?: number; terreno?: boolean } = {},
): { pontos: number[]; casos: CasoConhecido[] } {
  const densidade = opcoes.densidade ?? 800
  const ruido = opcoes.ruido ?? 0.002
  const aleatorio = aleatorioComSemente(opcoes.semente ?? 1)
  const pontos: number[] = []
  const casos: CasoConhecido[] = []

  const porAltura = [...pilares].sort((a, b) => altura(b) - altura(a))
  const [fugido, desaprumado, muitoFugido, escondido] = porAltura
  const vigaComprida = [...vigas].sort((a, b) => area(b) - area(a))[0]

  for (const e of pilares) {
    let mexer: Mexer | null = null
    if (e === fugido) {
      mexer = (x, y, z) => [x + 0.015, y, z]
      casos.push({ localId: e.localId, descricao: 'fugido 15 mm na horizontal, dentro da tolerância' })
    } else if (e === desaprumado) {
      const y0 = base(e)
      mexer = (x, y, z) => [x + 0.004 * (y - y0), y, z]
      casos.push({ localId: e.localId, descricao: 'desaprumado 4 mm/m a partir da base' })
    } else if (e === muitoFugido) {
      mexer = (x, y, z) => [x, y, z + 0.035]
      casos.push({ localId: e.localId, descricao: 'fugido 35 mm na horizontal, fora da tolerância' })
    } else if (e === escondido) {
      casos.push({ localId: e.localId, descricao: 'sem pontos, como um pilar escondido atrás de uma parede' })
      continue
    }
    amostrarElemento(e, densidade, ruido, aleatorio, pontos, mexer)
  }
  for (const e of vigas) {
    let mexer: Mexer | null = null
    if (e === vigaComprida) {
      mexer = (x, y, z) => [x, y - 0.025, z]
      casos.push({ localId: e.localId, descricao: 'descida 25 mm, fora da tolerância' })
    }
    amostrarElemento(e, densidade, ruido, aleatorio, pontos, mexer)
  }

  // Um pouco de terreno à volta, que não é de elemento nenhum. É o que
  // um levantamento real traz, e o que a comparação tem de ignorar.
  if (opcoes.terreno !== false && pontos.length > 0) {
    let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, y0 = Infinity
    for (let i = 0; i < pontos.length; i += 3) {
      x0 = Math.min(x0, pontos[i]); x1 = Math.max(x1, pontos[i])
      y0 = Math.min(y0, pontos[i + 1])
      z0 = Math.min(z0, pontos[i + 2]); z1 = Math.max(z1, pontos[i + 2])
    }
    const n = Math.round(pontos.length / 3 / 10)
    for (let k = 0; k < n; k += 1) {
      pontos.push(
        x0 - 5 + aleatorio() * (x1 - x0 + 10),
        y0 - 0.5 - aleatorio() * 0.5,
        z0 - 5 + aleatorio() * (z1 - z0 + 10),
      )
    }
  }
  return { pontos, casos }
}
